import React, { Fragment, FC, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { orderBy } from 'lodash';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import GET_KEYBOARDING_CLASSROOM_SETTINGS from 'graph/settings/key/get-keyboarding-classroom-settings-query';
import UPDATE_KEY_CLASSROOM_HOME_USE_SETTING from 'graph/settings/key/update-key-classroom-home-use-setting-mutation';
import { updateCache } from 'graph/update-cache';
import useStore from 'store';
import {
  Classroom,
  HomeUseClassroomDataInput,
  KeyClassroomSettingHomeUseData,
  KeyClassroomSettingHomeUseStudentData,
  KeySettingHomeUseData,
  Student
} from 'types/schema-generated-types';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';
import EmailInput, { OnChangeArgs } from 'components/@common/Inputs/EmailInput';

export interface HomeUseSettingRow {
  productCurriculumCode: string;
  contactEmail: string;
  studentId: number;
  firstName: string;
  lastName: string;
  password: string;
}

interface HomeAccessProps {
  classroom: Classroom;
  expanded: string | null;
  setting: KeyClassroomSettingHomeUseData | null;
  handleChangeExpanded: (panel: string | null) => () => void;
}

interface HomeAccessFormProps {
  students: HomeUseSettingRow[];
  handleEmailContactChange: (id: number) => (args: OnChangeArgs) => void;
}

// const EMAIL_TEMPLATE =
//   'Dear Parent or Guardian\n\nIn our class this year, STUDENT_NAME is using Keyboarding Without Tears to learn keyboarding skills, computer literacy and digital citizenship. One of the features of the program is that students can use the student license they have from school for practice outside the school. To access the program, you will need a laptop or desktop with internet access.';

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: { padding: theme.spacing(2), display: 'flex' },
    studentForm: { display: 'flex', marginTop: theme.spacing(2) },
    studentHeaderRow: { flex: '25%' },
    studentInfo: { display: 'flex', flexDirection: 'column', flex: '25%' },
    studentLabel: { fontWeight: theme.typography.fontWeightBold, paddingLeft: theme.spacing(0.6) },
    studentField: { marginLeft: theme.spacing(1), flex: '25%' },
    studentTemplate: { marginTop: theme.spacing(3) },
    switchContainer: { marginLeft: 0, marginTop: theme.spacing(2) },
    label: {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(1),
      '&.Mui-focused': {
        color: `${theme.palette.common.black} !important`
      }
    },
    textArea: {
      '& > .Mui-disabled': {
        backgroundColor: `${theme.palette.common.white} !important`
      }
    }
  };
});

const formatStudentRows = (
  studentSettings?: KeyClassroomSettingHomeUseStudentData[] | null,
  students?: Student[] | null
): HomeUseSettingRow[] => {
  if (!studentSettings || !students) return [];

  const rows = studentSettings.reduce((acc, studentSetting) => {
    const student = students.find(s => s.id === studentSetting.studentId);
    if (!student || !student.password) return acc;

    const { productCurriculumCode, homeUse } = studentSetting;
    const homeUseSetting = homeUse as KeySettingHomeUseData & {
      [key: string]: keyof KeySettingHomeUseData;
    };

    return [
      ...acc,
      {
        studentId: student.id,
        firstName: student.firstName,
        lastName: student.lastName,
        password: student.password,
        productCurriculumCode,
        contactEmail: (!!homeUseSetting?.[productCurriculumCode] && homeUse?.contactEmail) || ''
      }
    ];
  }, [] as HomeUseSettingRow[]);

  return orderBy(
    rows,
    [row => row.lastName.toLowerCase(), row => row.firstName.toLowerCase()],
    'asc'
  );
};

const getClassroomKeySettingsMutationCacheUpdate = (classroomId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_KEYBOARDING_CLASSROOM_SETTINGS,
      variables: {
        where: {
          classroomId
        }
      },
      reducer: (
        cachedData,
        { updateKeyClassroomSettingHomeUse }
      ): { keyClassroomSettings: any } => {
        return {
          keyClassroomSettings: {
            ...cachedData?.keyClassroomSettings,
            homeUse: {
              enabled: updateKeyClassroomSettingHomeUse.length > 0
            }
          }
        };
      }
    }
  ])
});

const HomeAccess: FC<HomeAccessProps> = ({
  classroom,
  expanded,
  setting,
  handleChangeExpanded
}) => {
  const classes = useStyles();
  const [, { openAlert }] = useStore();
  const { t } = useTranslation('HomeAccessSetting');
  const [updateKeyClassroomSettingHomeUse] = useMutation(
    UPDATE_KEY_CLASSROOM_HOME_USE_SETTING,
    getClassroomKeySettingsMutationCacheUpdate(classroom.id)
  );

  const [enableHomeAccess, setEnableHomeAccess] = useState(setting?.enabled || false);
  const [contactErrorMap, setContactErrorMap] = useState<any>({});
  const [contactEmails, setContactEmails] = useState<Array<HomeUseSettingRow>>(
    formatStudentRows(setting?.students, classroom.students)
  );

  const handleToggle = (): void => setEnableHomeAccess(!enableHomeAccess);

  const formatStudentPayload = (
    enableHomeAccess: boolean,
    emails: HomeUseSettingRow[]
  ): HomeUseClassroomDataInput[] =>
    emails.map(
      ({ studentId, firstName, lastName, password, contactEmail, productCurriculumCode }) => ({
        firstName,
        lastName,
        password,
        studentId,
        productCurriculumCode,
        ...(enableHomeAccess && { contactEmail })
      })
    );

  const handleEmailContactValidation = (id: number) => (err: string | null): void => {
    if (!err) {
      delete contactErrorMap[id];
      setContactErrorMap(contactErrorMap);
      return;
    }

    setContactErrorMap((prevState: any) => ({ ...prevState, [id]: err }));
  };

  const handleEmailContactChange = useCallback(
    (id: number) => ({ value }: OnChangeArgs): void => {
      const updatedEmails = contactEmails.map(customEmail => {
        if (customEmail.studentId === id) {
          return { ...customEmail, contactEmail: value };
        }
        return customEmail;
      });
      setContactEmails(updatedEmails);
    },
    [contactEmails, setContactEmails]
  );

  const handleOnCancel = (): void => {
    setEnableHomeAccess(setting?.enabled || false);
    setContactEmails(formatStudentRows(setting?.students, classroom.students));
  };

  const handleOnConfirm = async (): Promise<void> => {
    try {
      await updateKeyClassroomSettingHomeUse({
        variables: {
          data: {
            classroomId: classroom.id,
            value: formatStudentPayload(enableHomeAccess, contactEmails)
          }
        }
      });
      openAlert(getSettingSuccessAlert('Home Access', t));
    } catch (error) {
      // TODO
      console.log('ERROR');
    }
  };

  return (
    <SettingsCard
      title={t('Home Access')}
      panel="homeAccess"
      expanded={expanded}
      handleChangeExpanded={handleChangeExpanded}
      description={t('Send student login information via email to parents and/or guardians.')}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
      applyLabel={enableHomeAccess ? t('Apply Changes & Send Email') : t('Apply Changes')}
      disabled={Boolean(Object.keys(contactErrorMap)?.length)}
    >
      <FormControl component="fieldset" className={classes.container}>
        <FormLabel component="label" className={classes.label}>
          {t('Home Access')}
        </FormLabel>
        <Typography variant="body2">
          {t(
            'By enabling Home Access, an email will be sent from technology@lwtears.com to the parent / guardian outlining the access path and features of Keyboarding Without Tears.'
          )}
        </Typography>
        <FormControlLabel
          className={classes.switchContainer}
          control={
            <Switch
              checked={enableHomeAccess}
              onChange={handleToggle}
              name="homeAccess"
              color="primary"
            />
          }
          label={enableHomeAccess ? t('Enabled') : t('Disabled')}
        />
        {enableHomeAccess && (
          <div>
            <Fragment>
              <div className={classes.studentForm}>
                <div className={classes.studentHeaderRow}>
                  <Typography variant="body2" className={classes.studentLabel}>
                    {t('STUDENT NAME')}
                  </Typography>
                </div>
                <div className={classes.studentHeaderRow}>
                  <Typography variant="body2" className={classes.studentLabel}>
                    {t('PARENT EMAIL')}
                  </Typography>
                </div>
              </div>
              {contactEmails.map(student => {
                return (
                  <div
                    key={`student-home-use-form-${student.studentId}`}
                    className={classes.studentForm}
                  >
                    <div className={classes.studentInfo}>
                      <Typography
                        variant="body1"
                        component="p"
                      >{`${student.firstName} ${student.lastName}`}</Typography>
                    </div>
                    <EmailInput
                      variant={'outlined'}
                      placeholder={t('Email Address')}
                      onChange={handleEmailContactChange(+student.studentId)}
                      onValidation={handleEmailContactValidation(+student.studentId)}
                      error={contactErrorMap?.[student.studentId]}
                      value={student.contactEmail}
                      required={false}
                    />
                  </div>
                );
              })}
            </Fragment>
          </div>
        )}
      </FormControl>
    </SettingsCard>
  );
};

export default HomeAccess;
